import React, { useEffect, useState } from "react";
import { http, streamSSE } from "../lib/api";
import MarkdownLite from "../components/MarkdownLite";
import { ShieldCheck, FileText, Question, ChartBar, Sparkle } from "@phosphor-icons/react";

const TABS = [
  { key: "compliance", label: "Compliance and Policy Check", icon: ShieldCheck },
  { key: "jd", label: "Job Description", icon: FileText },
  { key: "interview", label: "Interview Questions", icon: Question },
  { key: "dashboard", label: "Dashboard", icon: ChartBar },
];

export default function JobRequisition({ requisitionNumber }) {
  const [tab, setTab] = useState("compliance");
  const [requisition, setRequisition] = useState(null);
  const [allReqs, setAllReqs] = useState([]);

  useEffect(() => {
    http.get("/fusion/requisitions").then((r) => setAllReqs(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!requisitionNumber) {
      setRequisition(null);
      return;
    }
    http
      .get(`/fusion/requisitions/${requisitionNumber}`)
      .then((r) => setRequisition(r.data.item))
      .catch(() => setRequisition(null));
  }, [requisitionNumber]);

  return (
    <div className="space-y-5" data-testid="job-requisition-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Vaku · Agentic AI</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
            Complete Job Requisition
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Generated content streams directly from Claude Sonnet 4.5.
          </p>
        </div>
        {requisition && (
          <div className="card p-3 text-xs">
            <div className="label-eyebrow !text-[10px]">Active Req</div>
            <div className="font-mono font-bold text-base">#{requisition.RequisitionNumber}</div>
            <div className="text-slate-600">{requisition.Title}</div>
            <div className="text-slate-400">{requisition.Location}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="card overflow-hidden">
        <div className="tabs-bar px-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`req-tab-${t.key}`}
                className={`tab-btn flex items-center gap-2 ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                <Icon size={14} weight={tab === t.key ? "fill" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="p-6">
          {tab === "compliance" && <ComplianceTab requisition={requisition} />}
          {tab === "jd" && <JobDescriptionTab requisition={requisition} />}
          {tab === "interview" && <InterviewQTab requisition={requisition} />}
          {tab === "dashboard" && <DashboardTab requisitions={allReqs} />}
        </div>
      </div>
    </div>
  );
}

function useStream(endpoint, defaultBody) {
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const run = async (body) => {
    setOutput("");
    setStreaming(true);
    await streamSSE(endpoint, body || defaultBody, {
      onDelta: (_d, full) => setOutput(full),
      onDone: () => setStreaming(false),
      onError: () => setStreaming(false),
    });
  };
  return { output, streaming, run, setOutput };
}

function GenerateButton({ onClick, disabled, label = "Generate with AI" }) {
  return (
    <button
      data-testid="ai-generate-btn"
      className="btn btn-primary"
      onClick={onClick}
      disabled={disabled}
    >
      <Sparkle size={14} weight="fill" />
      {disabled ? "Streaming…" : label}
    </button>
  );
}

function OutputBox({ output, streaming, placeholder }) {
  return (
    <div className="border bg-[#fafbfd] rounded-sm p-5 min-h-[260px]">
      {output ? (
        <>
          <MarkdownLite text={output} />
          {streaming && <span className="cursor-blink" />}
        </>
      ) : (
        <div className="text-slate-400 text-sm text-center py-12">{placeholder}</div>
      )}
    </div>
  );
}

function ComplianceTab({ requisition }) {
  const [notes, setNotes] = useState("");
  const { output, streaming, run } = useStream("/ai/compliance-check");
  const ready = !!requisition;
  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl font-bold">Compliance and Policy Check</h2>
      <p className="text-sm text-slate-500">
        Vaku will audit against Saudi Labor Law, Saudization (Nitaqat), GOSI/WPS, and your internal hiring policies.
      </p>
      <textarea
        data-testid="compliance-notes"
        className="input"
        placeholder="Optional notes (e.g., contract type, budget, special conditions)…"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="flex gap-2">
        <GenerateButton
          disabled={streaming || !ready}
          onClick={() =>
            run({
              requisitionNumber: requisition.RequisitionNumber,
              jobTitle: requisition.Title,
              department: requisition.Department,
              location: requisition.Location,
              notes,
            })
          }
          label="Run Compliance Check"
        />
        {!ready && <span className="text-xs text-amber-600 self-center">Enter a Requisition Number in the sidebar.</span>}
      </div>
      <OutputBox output={output} streaming={streaming} placeholder="Compliance results will appear here." />
    </div>
  );
}

function JobDescriptionTab({ requisition }) {
  const [seniority, setSeniority] = useState("Mid");
  const [notes, setNotes] = useState("");
  const { output, streaming, run } = useStream("/ai/job-description");
  const ready = !!requisition;
  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl font-bold">Job Description (GenAI)</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Seniority</label>
          <select
            data-testid="jd-seniority"
            className="input"
            value={seniority}
            onChange={(e) => setSeniority(e.target.value)}
          >
            {["Junior", "Mid", "Senior", "Lead", "Manager"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-1">Notes (optional)</label>
          <input
            data-testid="jd-notes"
            className="input"
            placeholder="e.g., bilingual Arabic/English required"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <GenerateButton
          disabled={streaming || !ready}
          onClick={() =>
            run({
              requisitionNumber: requisition.RequisitionNumber,
              jobTitle: requisition.Title,
              department: requisition.Department,
              location: requisition.Location,
              seniority,
              notes,
            })
          }
          label="Generate JD"
        />
        {!ready && <span className="text-xs text-amber-600 self-center">Enter a Requisition Number in the sidebar.</span>}
      </div>
      <OutputBox output={output} streaming={streaming} placeholder="Generated JD will appear here." />
    </div>
  );
}

function InterviewQTab({ requisition }) {
  const [count, setCount] = useState(5);
  const [focus, setFocus] = useState("");
  const { output, streaming, run } = useStream("/ai/interview-questions");
  const ready = !!requisition;
  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl font-bold">Interview Question (GenAI)</h2>
      <p className="text-sm text-slate-500">
        Write N interview questions and include scoring rubrics.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="label-eyebrow block mb-1"># Questions</label>
          <input
            data-testid="iq-count"
            type="number"
            min={3}
            max={15}
            className="input"
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value || "5", 10))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-1">Focus area (optional)</label>
          <input
            data-testid="iq-focus"
            className="input"
            placeholder="e.g., technical HCM, behavioural leadership"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <GenerateButton
          disabled={streaming || !ready}
          onClick={() =>
            run({
              requisitionNumber: requisition.RequisitionNumber,
              jobTitle: requisition.Title,
              count,
              focus,
            })
          }
          label="Generate Questions"
        />
        {!ready && <span className="text-xs text-amber-600 self-center">Enter a Requisition Number in the sidebar.</span>}
      </div>
      <OutputBox output={output} streaming={streaming} placeholder="Interview questions will stream here." />
    </div>
  );
}

function DashboardTab({ requisitions }) {
  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl font-bold">Requisition Dashboard</h2>
      <p className="text-sm text-slate-500">Requisitions tracked in Vaku.</p>
      <div className="card overflow-hidden">
        <table className="data-table" data-testid="requisitions-table">
          <thead>
            <tr>
              <th>Req #</th>
              <th>Title</th>
              <th>Department</th>
              <th>Location</th>
              <th>Hiring Manager</th>
              <th>Openings</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {requisitions.map((r) => (
              <tr key={r.RequisitionNumber}>
                <td className="font-mono font-semibold">#{r.RequisitionNumber}</td>
                <td className="font-medium">{r.Title}</td>
                <td>{r.Department}</td>
                <td>{r.Location}</td>
                <td>{r.HiringManager || "—"}</td>
                <td>{r.Openings || 1}</td>
                <td>
                  <span className={`pill ${r.Status === "Open" ? "pill-offer" : "pill-new"}`}>
                    {r.Status || "Open"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
